import { DependencyList, useCallback, useMemo } from 'react'
import {
  ApiGetWithToken,
  useApiFetch,
  useApiFetchList
} from 'shared/api'
import {
	searchApiBy,
	searchsSisetuApiBy,
	searchsTrialApiBy
} from 'interface/endpoints'
import {
	TrialResult,
	TrialResultList
} from './types'
import { safeParseDate } from 'shared/util'
import { useUpdateTime } from 'interface/util'

export * from './types'

export const useListTrial = (
	searchId: number | undefined | null,
	sisetuCode: string | undefined | null,
  option: {
    updateTime?: string
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: TrialResult[]) => void
  } = {},
  deps?: DependencyList
) => {
	const { updateTime, partialLimit, onPreFetch, onFetched } = option
  const cacheTime = useMemo(() => safeParseDate(updateTime)?.getTime(), [updateTime])
  return useApiFetchList<TrialResult, TrialResult[]>(
    useCallback(
      (apiAccess, cancelToken) => {
        if (!searchId) return 0
        if (onPreFetch) onPreFetch()
        return ApiGetWithToken<number>(
          apiAccess,
          sisetuCode ? searchsSisetuApiBy(searchId, sisetuCode, '/trial/count', { cacheTime }) :
          searchApiBy(searchId, '/trial/count', { cacheTime }),
          cancelToken
        )
      },
      [searchId, cacheTime, sisetuCode, onPreFetch]
    ),
    useCallback(
      (apiAccess, cancelToken, offset, limit) => {
        if (!searchId) return null
        const wrapper = async () => {
          const list = await ApiGetWithToken<TrialResultList>(
            apiAccess,
            sisetuCode ? searchsSisetuApiBy(searchId, sisetuCode, '/trial/list', { cacheTime, offset, limit }) :
            searchApiBy(searchId, '/trial/list', { cacheTime, offset, limit }),
            cancelToken
          )
          return list?.list
        }
        return wrapper()
      },
      [searchId, cacheTime, sisetuCode]
    ),
    useCallback(
      (result) => {
        if (onFetched) onFetched(result)
        return result
      },
      [onFetched]
    ),
    partialLimit ?? 1000,
    deps
  )
}

export const useGetTrial = (
  searchId: number | null | undefined,
	trialId: number | null | undefined,
  option: {
    defaultTrial?: TrialResult
    updateTime?: string
  } = {}
) => {
  const { defaultTrial, updateTime } = option
  const t = useUpdateTime(updateTime)
  return useApiFetch(
    useCallback(
      (apiAccess, cancelToken) => {
        if (!searchId || !trialId) return null
        if (defaultTrial && defaultTrial.trialId === trialId) return defaultTrial
        return ApiGetWithToken<TrialResult>(
          apiAccess,
          searchsTrialApiBy(searchId, trialId, undefined, { t }),
          cancelToken
        )
      },
      [searchId, t, trialId, defaultTrial]
    )
  )
}
